import { useEffect, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import {
  CompanyExpenseMoneyBucket,
  CompanyExpenseTypeAnalysisReport,
} from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCompanyExpenseTypeAnalysis } from './exportCompanyExpenseReports';

export function CompanyExpenseTypeAnalysisPage() {
  const labels = t().reports;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] =
    useState<CompanyExpenseTypeAnalysisReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpenseTypeAnalysisReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  function renderTable(
    title: string,
    rows: Array<{ label: string } & CompanyExpenseMoneyBucket>,
  ) {
    return (
      <DataTablePanel>
        <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">
          {title}
        </h3>
        <table className="min-w-full text-sm">
          <thead className={dataTableHeadClass}>
            <tr>
              <th className="px-4 py-3 text-left font-medium">
                {t().common.total}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {labels.lineCount}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {labels.expenseNet}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {labels.expenseVat}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {labels.expenseGross}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-line">
                <td className="px-4 py-2 font-medium">{row.label}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoney(row.net)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoney(row.vat)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoney(row.gross)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTablePanel>
    );
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          report ? (
            <ReportExportButtons
              disabled={!report}
              onExcel={() =>
                exportCompanyExpenseTypeAnalysis({ report, format: 'excel' })
              }
              onPdf={() =>
                exportCompanyExpenseTypeAnalysis({ report, format: 'pdf' })
              }
            />
          ) : null
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {loading ? (
        <LoadingSpinner message={t().common.loading} />
      ) : !report || report.totals.count === 0 ? (
        <ListEmptyState title={t().common.empty} />
      ) : (
        <div className="space-y-4">
          {renderTable(labels.companyExpenseTypeTitle, [
            {
              label: t().financial.invoice,
              ...report.byType.INVOICE,
            },
            { label: t().financial.cash, ...report.byType.CASH },
          ])}
          {renderTable(labels.companyExpenseSettlementTitle, [
            {
              label: labels.companyExpensePaid,
              ...report.bySettlement.paid,
            },
            {
              label: labels.companyExpenseRemaining,
              ...report.bySettlement.remaining,
            },
          ])}
        </div>
      )}
    </PageShell>
  );
}
